/**
 * Drives the custom-value price ladder — design doc "2c"
 * (`docs/features/product-catalogue/`).
 *
 * A preset configuration's ladder is instant: every (variant, quantity)
 * pair is already cached, so `getCatalogueItemPricePreview` resolves the
 * whole thing from cache in one call. A configuration carrying a genuine
 * custom value has no cache to read — the manufacturing cost has to be
 * live-costed via Tharstern first, one `cost-configuration` call per
 * quantity rung, before the price preview has anything to resolve.
 *
 * This hook does exactly that warming, and nothing else: it does not
 * compute or render a price itself (that stays "business logic stays
 * backend" — the caller re-resolves the price preview once a rung lands, so
 * every number shown is still a real backend figure, never a client-side
 * derivation). Behaviour:
 *
 * - Debounces `debounceMs` after the configuration last changed, so typing
 *   fires nothing until the orderer pauses.
 * - Fires one request per quantity rung, bounded to `maxConcurrent` in
 *   flight — Tharstern serialises, so more concurrency buys nothing
 *   (design doc: measured, 2 in flight doubles latency for no throughput
 *   gain).
 * - Reports each rung as it settles (`onRungSettled`), so the caller can
 *   progressively re-render — never waiting for the whole ladder before
 *   showing anything.
 * - Generation-guards every apply: a configuration change bumps the
 *   generation immediately (synchronously, ahead of the debounce), and any
 *   in-flight result from an abandoned generation is dropped rather than
 *   painted or reported. Mirrors the closure-flag guard
 *   `CatalogueItemPage`'s own price-preview effect already uses, generalised
 *   to bounded concurrency — the same class of problem `useVariantCostRefresh`'s
 *   deadline-bounded poll (#1527) solves for a single in-flight request.
 */

import { useEffect, useRef, useState } from "react";
import type { VariantQuantityCost } from "../data/catalogue-types";
import { useDebouncedValue } from "./useDebouncedValue";

export type LadderRungStatus = "pending" | "settled";

export interface LadderRungState {
  quantity: number;
  status: LadderRungStatus;
  /** The landed cost row once settled — its own `status` carries the real
   *  outcome (fresh/failed/skipped), matching every other cost-refresh path.
   *  `null` while pending, or when the request itself failed (see
   *  `requestError`) rather than Tharstern rejecting the configuration. */
  cost: VariantQuantityCost | null;
  /** Set only when the request itself could not be completed (network,
   *  access denied, item not found) — never for a genuine Tharstern-side
   *  rejection, which lands as a normal settled `cost` whose own `status`
   *  is "failed". */
  requestError: string | null;
}

export interface UseCatalogueCustomValueLadderArgs {
  itemId: string;
  /** The full, locally-validated configuration to warm, or `null` when
   *  there is nothing to warm — no custom value is active, or an active one
   *  is incomplete or invalid (see `resolveCustomValueLadderConfiguration`).
   *  Passing `null` resets all rung state and fires no requests. */
  configuration: Record<string, string> | null;
  /** The quantity rungs to price — the item's own quantity breaks. */
  quantities: number[];
  costFn: (
    itemId: string,
    req: { configuration: Record<string, string>; quantity: number },
  ) => Promise<VariantQuantityCost>;
  /** Called once per rung as it settles, for the current generation only —
   *  the caller's hook for "re-resolve whatever reads the now-warmer cache"
   *  (typically: bump a price-preview refresh key). */
  onRungSettled?: (quantity: number, cost: VariantQuantityCost | null) => void;
  debounceMs?: number;
  maxConcurrent?: number;
}

export interface CatalogueCustomValueLadderResult {
  /** True from the moment a debounce starts for a non-null configuration
   *  until every rung has settled (or the configuration changes again). */
  warming: boolean;
  /** Per-rung state, ascending by quantity. Empty when `configuration` is
   *  `null`. */
  rungs: LadderRungState[];
}

const DEFAULT_DEBOUNCE_MS = 800;
const DEFAULT_MAX_CONCURRENT = 4;

export function useCatalogueCustomValueLadder(
  args: UseCatalogueCustomValueLadderArgs,
): CatalogueCustomValueLadderResult {
  const {
    itemId,
    configuration,
    quantities,
    costFn,
    onRungSettled,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    maxConcurrent = DEFAULT_MAX_CONCURRENT,
  } = args;

  // Stable string keys: `configuration` is a fresh object every render (the
  // page rebuilds it from state each time), but what should re-trigger
  // debouncing/fetching is its *content*, exactly like the page's own
  // `pricingKey` pattern for the price-preview effect.
  const configKey = configuration ? JSON.stringify(configuration) : null;
  const quantitiesKey = quantities.join(",");
  const debouncedKey = useDebouncedValue(configKey, debounceMs);

  const [rungs, setRungs] = useState<Map<number, LadderRungState>>(new Map());
  const [warming, setWarming] = useState(false);

  // Refs so the fetch effect can read the latest callback/fn without
  // re-running (and re-debouncing) whenever the caller passes a fresh
  // closure — only configKey/itemId/quantitiesKey should do that.
  const generationRef = useRef(0);
  const costFnRef = useRef(costFn);
  costFnRef.current = costFn;
  const onRungSettledRef = useRef(onRungSettled);
  onRungSettledRef.current = onRungSettled;

  // Resets synchronously the instant the active configuration changes —
  // ahead of the debounce delay — so anything still in flight for the
  // *previous* configuration is immediately disowned (its generation no
  // longer matches) even though its network request has not returned yet.
  useEffect(() => {
    generationRef.current += 1;
    if (configKey === null) {
      setRungs(new Map());
      setWarming(false);
      return;
    }
    setRungs(
      new Map(
        quantities.map((q) => [
          q,
          {
            quantity: q,
            status: "pending" as const,
            cost: null,
            requestError: null,
          },
        ]),
      ),
    );
    setWarming(true);
    // Intentionally keyed on configKey/itemId only: quantities is read for
    // its current value, not tracked for referential changes (quantitiesKey
    // below is the stable representation of its content).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, itemId]);

  // The actual fetch cycle, gated on the debounced key catching up to the
  // live one — so this only ever runs `debounceMs` after typing stops.
  useEffect(() => {
    if (debouncedKey === null || debouncedKey !== configKey) return;
    const myGeneration = generationRef.current;
    const config = configuration as Record<string, string>;
    const queue = [...quantities];

    async function worker(): Promise<void> {
      for (;;) {
        if (generationRef.current !== myGeneration) return;
        const quantity = queue.shift();
        if (quantity === undefined) return;
        let cost: VariantQuantityCost | null = null;
        let requestError: string | null = null;
        try {
          cost = await costFnRef.current(itemId, {
            configuration: config,
            quantity,
          });
        } catch (err) {
          requestError =
            err instanceof Error
              ? err.message
              : "Could not price this quantity";
        }
        if (generationRef.current !== myGeneration) return;
        setRungs((prev) => {
          const next = new Map(prev);
          next.set(quantity, {
            quantity,
            status: "settled",
            cost,
            requestError,
          });
          return next;
        });
        onRungSettledRef.current?.(quantity, cost);
      }
    }

    const workerCount = Math.min(maxConcurrent, quantities.length);
    void Promise.all(Array.from({ length: workerCount }, worker)).then(() => {
      if (generationRef.current === myGeneration) setWarming(false);
    });
    // configKey is deliberately also a dependency, not just debouncedKey: if
    // the orderer edits away from an already-warmed value and back again
    // within the debounce window, useDebouncedValue's own setState never
    // fires (its target value never actually changed), so debouncedKey alone
    // would never re-trigger this effect — leaving the first effect's reset
    // stuck on "pending" forever for a value that was already priced. With
    // configKey included, the render where it settles back onto a value
    // debouncedKey already matches re-evaluates the guard below and fires
    // immediately, rather than waiting out a second debounce for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, configKey, itemId, quantitiesKey, maxConcurrent]);

  return {
    warming,
    rungs: [...rungs.values()].sort((a, b) => a.quantity - b.quantity),
  };
}
